<script setup lang="ts">
    import RemissMottagareAutocomplete from "@/components/forms/reusables/RemissMottagareAutocomplete.vue";
    import { computed, ref } from "vue";
    import { useStore } from "@/store";
    import { getConfig, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import emitter from "tiny-emitter/instance";
    import KDialog from "@/components/generic/KDialog.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KDialogMessageList from "@/components/generic/KDialogMessageList.vue";

    const store = useStore();

    const anmalanId = ref<string | null>(null);

    const can = store.getters["ability/can"];

    const errors = ref({});
    const canForce = ref(false);

    const dialogRef = ref();
    const formRef = ref();

    const showDialog = ({ id }: { id: string }) => {
        if (!can({ action: "remiss", subject: "anmalan" })) {
            openDialog("Du har inte behörighet att skicka anmälan.", "error");
            return;
        }
        anmalanId.value = id;
        errors.value = {};
        canForce.value = false;
        dialogRef.value?.show();
    };

    defineExpose({
        show: showDialog,
        hide: dialogRef.value?.hide,
    });

    const initForm = () => {
        return {
            mottagare: null,
        };
    };

    const submitForm = async () => {
        if (!can({ action: "remiss", subject: "anmalan" })) {
            openDialog("Du har inte behörighet att skicka anmälan.", "error");
            return false;
        }

        const data = await formRef?.value?.manualSubmit();

        if (!data) {
            return false;
        }

        const force = hasErrors;

        try {
            let url = `/operation/anmalan/${anmalanId.value}/remiss/`;
            if (force.value) {
                url += "?force=true";
            }

            const response = await klinikenApi.post(
                url,
                {
                    mottagare: data.mottagare,
                },
                getConfig()
            );

            openDialog("Anmälan skickad", "info");
            emitter.emit("operationsanmalan-uppdaterad", response.data);
            return true;
        } catch (e: any) {
            if (e.response) {
                if (e.response.status === 422 || e.response.status === 418) {
                    errors.value = e.response.data;
                    canForce.value = e.response.status === 422;
                    return false;
                } else {
                    openDialog("Det gick inte att skicka anmälan.", "error");
                    return false;
                }
            } else {
                openDialog("Det gick inte att skicka anmälan.", "error");
                return false;
            }
        }
    };

    const cancelForm = async () => {
        return true;
    };

    const hasErrors = computed(() => Object.keys(errors.value).length > 0);

    const acceptContent = computed(() => (canForce.value ? "Tvinga skicka anmälan" : "Skicka anmälan"));

    const acceptVariant = computed(() => (canForce.value ? "warning" : "standard"));
</script>

<template>
    <KDialog
        ref="dialogRef"
        variant="basic"
        header-title="Skicka anmälan"
        cancel-content="Avbryt"
        :accept-content="acceptContent"
        :accept-variant="acceptVariant"
        :accept-action="submitForm"
        :cancel-action="cancelForm"
    >
        <template>
            <KForm ref="formRef" id="skicka-som-remiss-form" :initial-values-getter="initForm">
                <template #default>
                    <RemissMottagareAutocomplete validation="required" typ="operationsremiss" auto-position />

                    <KDialogMessageList :messages="errors" />
                </template>
            </KForm>
        </template>
    </KDialog>
</template>

<style scoped lang="scss"></style>
